import React, { useCallback, useEffect } from 'react'
import { AppSvg, FormattedHTMLMessage, icons, useNotification } from '@web/walter-ui-kit'
import { useUserSetting } from '@web/walter-lib/user-settings'
import { useIntl } from 'react-intl'
import { logPosthogEvent } from '@walter/shared/utils/posthog'
import { commonPosthogEvents } from '@walter/shared/constants/posthog'
import { KycStatus } from '@web/walter-lib/api'
import { lang } from './lang'
import styles from './style.module.css'
import { useAccountUserInfo, useKycRequestStatus } from '~api'
import { useSettingsModal } from '~modals/SettingsModal/useSettingsModal'
import { SettingsTabs } from '~modals/SettingsModal/Menu/const'
import { KycStatuses } from '~modals/SettingsModal/tabs/Identity/Level'
import { getKycLevelName } from '~utils/getKycLevelName'

const timeout = 10000

//eslint-disable-next-line max-lines-per-function
export const useKycNotifications = () => {
    const { setTab } = useSettingsModal()
    const { push } = useNotification()
    const intl = useIntl()
    const supportLink = intl.formatMessage(lang.contactSupportLink)
    const [{ pinShown, popupShownStatus }, setKycInfo] = useUserSetting('kycInfo')

    const { data: userInfo, isLoading: isUserInfoLoading } = useAccountUserInfo()
    const { data: kycStatus, isLoading: isKycStatusLoading } = useKycRequestStatus()

    const showNotification = userInfo?.verification_level === kycStatus?.transition?.current_level

    const checkKycStatus = useCallback(
        //eslint-disable-next-line complexity
        (status: KycStatus | undefined, currentLevel: number | undefined, showNotification: boolean) => {
            if (!showNotification) {
                return
            }

            if (status === KycStatuses.rejected && popupShownStatus !== KycStatuses.rejected) {
                push({
                    intent: 'notification',
                    title: (
                        <span className={styles.rejected}>
                            <FormattedHTMLMessage {...lang.rejectedTitle} />
                        </span>
                    ),
                    icon: (
                        <span className={styles.rejected}>
                            <AppSvg svgName={icons.ic18CircleCrossedOut} />
                        </span>
                    ),
                    timeout,
                    createdAt: Date.now(),
                    content: <FormattedHTMLMessage {...lang.rejectedMessage} />,
                    buttonProps: {
                        size: 's',
                        role: 'anchor',
                        fullWidth: true,
                        href: supportLink,
                        text: <FormattedHTMLMessage {...lang.contactSupport} />,
                        onClick: e => e.stopPropagation(),
                    },
                })

                logPosthogEvent(commonPosthogEvents.kycDataNotify, {
                    verdict: 'false',
                    current_level: getKycLevelName(currentLevel),
                })
            }

            if (
                status === KycStatuses.information_requested &&
                popupShownStatus !== KycStatuses.information_requested
            ) {
                push({
                    intent: 'notification',
                    title: (
                        <span className={styles.warning}>
                            <FormattedHTMLMessage {...lang.informationRequestedTitle} />
                        </span>
                    ),
                    icon: (
                        <span className={styles.warning}>
                            <AppSvg svgName={icons.ic12CircleExclamation} size={18} />
                        </span>
                    ),
                    timeout,
                    createdAt: Date.now(),
                    content: <FormattedHTMLMessage {...lang.informationRequestedMessage} />,
                    buttonProps: {
                        size: 's',
                        role: 'button',
                        fullWidth: true,
                        text: <FormattedHTMLMessage {...lang.informationRequestedButtonText} />,
                        onClick: () => setTab(SettingsTabs.identity),
                    },
                })
            }

            if (status === KycStatuses.verified) {
                logPosthogEvent(commonPosthogEvents.kycDataNotify, {
                    verdict: 'true',
                    current_level: getKycLevelName(currentLevel),
                })
            }

            setKycInfo({
                popupShownStatus: status ?? null,
                pinShown: status === KycStatuses.rejected ? pinShown : false,
            })
        },
        //eslint-disable-next-line react-hooks/exhaustive-deps
        [pinShown, popupShownStatus, supportLink]
    )

    useEffect(() => {
        if (isUserInfoLoading || isKycStatusLoading) {
            return
        }

        checkKycStatus(kycStatus?.status, userInfo?.verification_level, showNotification)
    }, [
        checkKycStatus,
        showNotification,
        isUserInfoLoading,
        kycStatus?.status,
        isKycStatusLoading,
        userInfo?.verification_level,
    ])
}
